import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, CircleDashed, XCircle, FileDown, Info, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { DspAreaDrawer } from '../components/dsp/DspAreaDrawer';
import { dspHubAPI, errMsg, saveBlob } from '../lib/regulatoryAPI';

export const BADGES = {
  Compliant: { icon: CheckCircle2, cls: 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300' },
  Partial: { icon: CircleDashed, cls: 'border-amber-300 bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300' },
  Gap: { icon: XCircle, cls: 'border-red-300 bg-red-50 text-red-800 dark:bg-red-950/40 dark:text-red-300' },
};

export const ReadinessBadge = ({ value, rule, testId }) => {
  const b = BADGES[value] || BADGES.Gap;
  return (
    <TooltipProvider><Tooltip><TooltipTrigger asChild>
      <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${b.cls}`} data-testid={testId}>
        <b.icon className="h-3.5 w-3.5" />{value}
      </span>
    </TooltipTrigger><TooltipContent className="max-w-xs text-xs">{rule}</TooltipContent></Tooltip></TooltipProvider>
  );
};

const Tile = ({ label, value, testId }) => (
  <Card><CardContent className="pt-5"><p className="text-xs text-muted-foreground">{label}</p><p className="mt-1 text-2xl font-semibold" data-testid={testId}>{value}</p></CardContent></Card>
);

export default function DspHub() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { dspHubAPI.readiness().then(setData).catch((e) => toast.error(errMsg(e))); }, []);
  const pdf = async () => {
    setBusy(true);
    try { saveBlob(await dspHubAPI.report(), 'osf-readiness-report.pdf'); toast.success('Readiness report generated'); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const t = data?.tiles;
  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6" data-testid="dsp-hub-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Compliance &amp; Audit Hub</h1>
          <p className="mt-1 text-muted-foreground">ATO DSP Operational Security Framework – Category B readiness, built from the DSP Compliance Centre registers.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild data-testid="dsp-hub-open-centre"><a href="/settings?tab=dsp"><ExternalLink className="mr-2 h-4 w-4" />DSP Compliance Centre</a></Button>
          <Button onClick={pdf} disabled={busy || !data} data-testid="dsp-hub-report-pdf"><FileDown className="mr-2 h-4 w-4" />Readiness report (PDF)</Button>
        </div>
      </div>
      <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950/30 dark:text-amber-200" data-testid="dsp-hub-note">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />{data?.note || 'Internal readiness tracking only – not ATO approval or certification.'}
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Tile label="Controls mapped / total" value={t ? `${t.controls_mapped} / ${t.controls_total}` : '–'} testId="dsp-hub-tile-controls" />
        <Tile label="Evidence items" value={t?.evidence_items ?? '–'} testId="dsp-hub-tile-evidence" />
        <Tile label="Open risks" value={t?.open_risks ?? '–'} testId="dsp-hub-tile-risks" />
        <Tile label="Outstanding actions" value={t?.outstanding_actions ?? '–'} testId="dsp-hub-tile-actions" />
      </div>
      <Card>
        <CardContent className="divide-y p-0" data-testid="dsp-hub-checklist">
          {(data?.areas || []).map((a) => (
            <button key={a.key} type="button" onClick={() => setOpen(a.key)} data-testid={`dsp-hub-area-${a.key}`}
              className="flex w-full items-center justify-between gap-4 px-5 py-3.5 text-left transition-colors hover:bg-muted/50">
              <div><p className="font-medium">{a.area}</p>
                <p className="text-xs text-muted-foreground">{a.controls_ready} of {a.controls_total} controls tested · {a.evidence_count} evidence item{a.evidence_count === 1 ? '' : 's'}</p></div>
              <ReadinessBadge value={a.badge} rule={data.rule} testId={`dsp-hub-badge-${a.key}`} />
            </button>
          ))}
        </CardContent>
      </Card>
      <DspAreaDrawer areaKey={open} onClose={() => setOpen(null)} />
    </div>
  );
}
